'use client';

import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Bot } from 'lucide-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Anchor } from '@components/common';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useIsClient } from '@hooks/useIsClient';

// Once-per-session flag: the card is shown at most once per browsing session (sessionStorage,
// cleared when the tab/session ends). Distinct from the old top banner's dismiss key.
const SESSION_KEY = 'bj.myBestie.jobCard.seen';

interface MyBestieJobCardProps {
  locale: Locale;
}

/**
 * @description My Bestie promo rendered as a single card mixed into the job results grid (replaces
 * the old top-of-list banner so the list has only one banner at the top). Shown only to
 * authenticated users WITHOUT the My Bestie service (`headerInfo.myBestieEnabled === false`).
 * Guests and users who already have the service never see it.
 *
 * Visibility is gated to once per session and the caller injects it only on the first page of
 * results (see the job list `renderBanner`), so it never duplicates across pages or shifts paging.
 */
export const MyBestieJobCard = ({ locale }: MyBestieJobCardProps) => {
  const { t } = useTranslation('common');
  const { headerInfoData, headerInfoLoading, isSessionAuthenticated } = useHeaderInfoData(locale);

  // Client-only: this card depends on sessionStorage + client header-info, which the server can't
  // know. Render nothing on the server and on the first client render (matching the server) to
  // avoid a hydration mismatch, then reveal after mount.
  const isClient = useIsClient();

  // Read the once-per-session flag at mount; if already seen this session, never show again.
  const [seen] = useState(
    () => typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_KEY) === '1',
  );
  const [closed, setClosed] = useState(false);

  const myBestieEnabled = !!headerInfoData?.myBestieEnabled;

  // Whether the card should actually be displayed. Only for authenticated users who don't have the
  // My Bestie service yet (hide while header-info resolves to avoid a flash). Not shown to guests.
  const shouldShow = isClient
    && !seen
    && !closed
    && isSessionAuthenticated
    && !headerInfoLoading
    && !myBestieEnabled;

  // Mark as seen only once it has actually been shown, so an earlier suppressed mount (logged-in
  // user with the service, or a still-loading mount) doesn't burn the once-per-session quota. The
  // local `seen` state stays false for the current mount, so it stays visible here.
  useEffect(() => {
    if (shouldShow && typeof window !== 'undefined') window.sessionStorage.setItem(SESSION_KEY, '1');
  }, [shouldShow]);

  if (!shouldShow) return null;

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setClosed(true);
  };

  return (
    <div className="relative flex h-full flex-col rounded-2xl border border-ink-border bg-surface p-4 text-ink transition-colors hover:bg-surface-100">
      <button
        type="button"
        onClick={handleClose}
        aria-label={t('global.btn.close')}
        className="absolute right-2 top-2 shrink-0 cursor-pointer rounded-full p-1 text-ink-light transition-colors hover:bg-surface-200 hover:text-ink"
      >
        <XMarkIcon className="size-5" />
      </button>
      <Anchor href="/my-bestie" styling="none" className="flex h-full flex-col gap-3 pr-6">
        <span className="w-fit rounded-full bg-secondary/15 p-2 text-secondary">
          <Bot className="size-5 shrink-0" />
        </span>
        <span className="text-sm font-bold text-ink-medium">
          {t('my.bestie.home.cta')}
        </span>
      </Anchor>
    </div>
  );
};
